import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable, throwError} from 'rxjs';
import {ApiService} from '../../api.service';
import { HttpClient } from "@angular/common/http";
import {NGXLogger} from "ngx-logger";
import {ActivatedRoute, Router} from "@angular/router";
import {LocaleService} from "../../../locale.service";
import {Backend, SingleResponse} from "@generated-backend-types";
import {catchError, map, tap} from "rxjs/operators";
import {CartInfo} from "../../../../entities/LocalStorageCart.entity";
import {isAfter} from "date-fns";
import Cart = Backend.App.Http.Resources.Ticketshop.Orders.Cart.Cart;
import CartData = Backend.App.Data.Orders.Cart.CartData;
import CartBookingQuestionData = Backend.App.Data.Orders.Cart.CartBookingQuestionData;
import CartItemData = Backend.App.Data.Orders.Cart.CartItemData;
import ArticlePrice = Backend.App.Http.Resources.Ticketshop.Articles.ArticlePrice;
import PublicErrorCodeEnum = Backend.App.Enums.PublicErrorCodeEnum;
import {StorageService} from "../../../storage.service";


@Injectable({
  providedIn: 'root'
})
export class CartService extends ApiService {
  private endpoint = '/orders/carts';

  public ticketCount$ = new BehaviorSubject<number | undefined >(undefined);

  private store(cartData: CartData): Observable<SingleResponse<Cart>> {
    return this.http.post<SingleResponse<Cart>>(this.endpoint, cartData).pipe(
      catchError(err => {
        if(
          err.error?.data?.error?.code === PublicErrorCodeEnum.CART_EXPIRED_EXCEPTION
        ) {
          this.deleteCartInfo()
          this.ticketCount$.next(0);
        }
        return throwError(err);
      }),
      tap(res => {
        let count = 0;
        res.data.cart_items.forEach(cartItem => {
          count += cartItem.quantity;
        });
        this.storeCartInfo(res.data);
        this.ticketCount$.next(count);
      })
    )
  }

  public show(cartId: string): Observable<SingleResponse<Cart>> {
    return this.http.get<SingleResponse<Cart>>(this.endpoint + '/' + cartId).pipe(
      catchError(err => {
        if(
          err.error?.data?.error?.code === PublicErrorCodeEnum.CART_EXPIRED_EXCEPTION
        ) {
          this.deleteCartInfo()
          this.ticketCount$.next(0);
        }
        return throwError(err);
      }),
      tap(res => {
        let count = 0;
        res.data.cart_items.forEach(cartItem => {
          count += cartItem.quantity;
        });
        this.storeCartInfo(res.data);
        this.ticketCount$.next(count);
      })
    )
  }

  public update(cartId: string, cartData: CartData): Observable<SingleResponse<Cart>> {
    return this.http.put<SingleResponse<Cart>>(this.endpoint + '/' + cartId, cartData).pipe(
      catchError(err => {
        if(
          err.error?.data?.error?.code === PublicErrorCodeEnum.CART_EXPIRED_EXCEPTION
        ) {
          this.deleteCartInfo()
          this.ticketCount$.next(0);
        }
        return throwError(err);
      }),
      tap(res => {
        let count = 0;
        res.data.cart_items.forEach(cartItem => {
          count += cartItem.quantity;
        });
        this.storeCartInfo(res.data);
        this.ticketCount$.next(count);
      })
    )
  }

  public clearCart(cartId: string): Observable<SingleResponse<Cart>> {
    return this.http.delete<SingleResponse<Cart>>(this.endpoint + '/' + cartId + '/clear').pipe(
      catchError(err => {
        if(
          err.error?.data?.error?.code === PublicErrorCodeEnum.CART_EXPIRED_EXCEPTION
        ) {
          this.deleteCartInfo()
          this.ticketCount$.next(0);
        }
        return throwError(err);
      }),
      tap(res => {
        let count = 0;
        res.data.cart_items.forEach(cartItem => {
          count += cartItem.quantity;
        });
        this.storeCartInfo(res.data);
        this.ticketCount$.next(count);
      })
    )
  }



  protected storageKey: string = 'cart_info';

  private cart: Cart | undefined;


  public getCartInfo(): CartInfo | null {
    return this.storageService.get(this.storageKey) ?? null;
  }

  public deleteCartInfo() {
    this.storageService.delete(this.storageKey);
    this.ticketCount$.next(undefined);
    this.cart = undefined;
  }

  public addAllToCart(
    itemsToAdd: CartItemData[],
    itemsToAddRelatedPriceList: ArticlePrice[],
    bookingQuestionsToAdd: CartBookingQuestionData[] = [],
    bookingQuestionSlotId: string = ''
  ): Observable<Cart> {
    // Map all current cart items into cart item data


    const currentCartItems: CartItemData[] = this.cart?.cart_items.map((cartItem) => {
      const newCartItem: CartItemData = {
        article_price_id: cartItem.article_price_id,
        booking_slot_id: cartItem.booking_slot_id,
        quantity: cartItem.quantity,
        discount_reason_id: cartItem.discount_reason_id,
        voucher_id: cartItem.voucher_id
      };
      if(cartItem.vc_booking) {
        newCartItem.vehicle_consultation_data = cartItem.vc_booking
      }
      return newCartItem;
    }) ?? [];

    // Remove all Booking Questions of the Slot that's going to be added &
    // Map all leftover Booking Questions into Cart Booking Question Data
    const currentBookingQuestions: CartBookingQuestionData[] = [];
    this.cart?.cart_booking_questions.forEach(
      (cartBookingQuestion) => {
        if (cartBookingQuestion.booking_slot_id !== bookingQuestionSlotId) {
          const cartBookingQuestionData: CartBookingQuestionData = {
            booking_slot_id: cartBookingQuestion.booking_slot_id,
            question_id: cartBookingQuestion.question_id,
            answer_ids: [],
            custom_answer: null
          };

          // Potential Error Source if for whatever reason we don't get answers back, or answers is empty
          if (cartBookingQuestion.answers && cartBookingQuestion.answers.length > 1) {
            cartBookingQuestionData.answer_ids = cartBookingQuestion.answers.map(answer => answer.booking_question_answer_id);
          } else if (cartBookingQuestion.answers && cartBookingQuestion.answers.length === 1) {
            if (cartBookingQuestion.answers[0].booking_question_answer_id !== null) {
              cartBookingQuestionData.answer_ids = [cartBookingQuestion.answers[0].booking_question_answer_id];
            } else {
              cartBookingQuestionData.custom_answer = cartBookingQuestion.answers[0].custom;
            }
          }

          currentBookingQuestions.push(cartBookingQuestionData);
        }
      }
    );

    const mergedCartData = this.mergeCartData(
      currentCartItems,
      itemsToAdd,
      itemsToAddRelatedPriceList,
      currentBookingQuestions,
      bookingQuestionsToAdd
    );

    if (this.cart?.id === undefined) {
      return this.createCart(mergedCartData);
    }
    return this.update(this.cart.id, mergedCartData).pipe(map(res => res.data))
  }

  private mergeCartData(
    currentCartItems: CartItemData[],
    itemsToAdd: CartItemData[],
    itemsToAddRelatedPriceList: ArticlePrice[],
    currentBookingQuestions: CartBookingQuestionData[],
    bookingQuestionsToAdd: CartBookingQuestionData[]
  ): CartData {
    const mergedCartItems: CartItemData[] = [...currentCartItems];
    let mergedCartBookingQuestions: CartBookingQuestionData[] = [...currentBookingQuestions];

    itemsToAdd.forEach((newItem) => {
      const existingItemIndex = mergedCartItems.findIndex(
        (item) => item.article_price_id === newItem.article_price_id && item.booking_slot_id === newItem.booking_slot_id
      );

      if (existingItemIndex !== -1) {
        const existingItem = mergedCartItems[existingItemIndex];
        if (newItem.discount_reason_id) {
          existingItem.discount_reason_id = newItem.discount_reason_id;
        }

        const relatedPriceObject = itemsToAddRelatedPriceList.find(
          (price) => price.id === newItem.article_price_id
        );

        if (relatedPriceObject) {
          const mergedQuantity = existingItem.quantity + newItem.quantity;
          existingItem.quantity = Math.min(
            relatedPriceObject.max_quantity,
            Math.max(relatedPriceObject.min_quantity, mergedQuantity)
          );
        }
      } else {
        mergedCartItems.push(newItem);
      }
    });

    mergedCartBookingQuestions = [...mergedCartBookingQuestions, ...bookingQuestionsToAdd];

    return {
      cart_items: mergedCartItems,
      booking_questions: mergedCartBookingQuestions,
      gift_voucher_codes: []
    };
  }


  private storeCartInfo(cart: Cart): void {
    this.cart = cart;
    this.storageService.set(
      this.storageKey,
      {id: cart.id, expires_at: cart.expires_at} as CartInfo
    );
  }

  private createCart(cartData: CartData): Observable<Cart> {
    this.deleteCartInfo();

    return this.store(cartData).pipe(
      map(res => {
          return res.data
        }
      ));
  }


  constructor(
    public override http: HttpClient,
    public override log: NGXLogger,
    public localeService: LocaleService,
    public router: Router,
    public activatedRoute: ActivatedRoute,
    private readonly storageService: StorageService,
  ) {
    super(http, log);

    const storedCart = this.getCartInfo();

    console.log('load storedCart', storedCart);
    if (storedCart && !isAfter(new Date(), new Date(storedCart.expires_at))) {
      this.show(storedCart.id).subscribe({
        next: res => {
          this.cart = res.data;

        },
        error: err => {
          switch (err.status) {
            case 404: // Cart expired or not found
              break;
            case 422: // missing available slots, cart already used for order, etc.
              break;
            default: // Any other error
              console.error(err);
          }
        }
      });
    } else {
      this.deleteCartInfo();
    }
  }
}
